import * as React from 'react';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import { getUser } from '../../../store/slices/userSlice';
import { isManagement, canEditCompanyProfile } from '../../../utils/permissions';
import CompanyProfileSection from '../../../components/company/CompanyProfileSection';
import MobileBottomTabsSection from './MobileBottomTabsSection';
import NotificationPreferencesSection from './NotificationPreferencesSection';
import { getAllStatuses, getStatusChipStyles, getStatusLabel } from '../../../utils/jobStatuses';
import {
  getJobStatusColor,
  setJobStatusColor,
  resetJobStatusColor,
  resetAllJobStatusColors,
  hasJobStatusColorOverride,
  JOB_STATUS_COLORS_CHANGED_EVENT,
} from '../../../utils/jobStatusColors';
import {
  fetchStoreNameSuggestions,
  addStoreNameSuggestion,
  updateStoreNameSuggestion,
  removeStoreNameSuggestion,
  sortStoreNameOptions,
} from '../../../utils/storeNameSuggestions';
import {
  fetchJobTags,
  addJobTag,
  updateJobTag,
  removeJobTag,
  sortTagOptions,
  getTagChipSx,
} from '../../../utils/jobTagSuggestions';

function SettingsSection({ title, description, defaultExpanded = false, children, isDarkMode }) {
  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      disableGutters
      elevation={0}
      sx={{
        borderRadius: '8px !important',
        border: '1px solid',
        borderColor: isDarkMode ? '#334155' : '#e0e0e0',
        backgroundColor: 'background.paper',
        '&:before': { display: 'none' },
        '&.Mui-expanded': { margin: 0 },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: 'text.secondary' }} />}
        sx={{
          px: 3,
          py: 1,
          minHeight: 56,
          '&.Mui-expanded': { minHeight: 56 },
          '& .MuiAccordionSummary-content': { my: 1.5 },
        }}
      >
        <Box>
          <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: 'text.primary' }}>
            {title}
          </Typography>
          {description && (
            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem', mt: 0.25 }}>
              {description}
            </Typography>
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 3, pt: 0, pb: 3 }}>
        {children}
      </AccordionDetails>
    </Accordion>
  );
}

function StatusColorRow({ status, isDarkMode, colorsRevision }) {
  const statusId = status.id;
  const [hex, setHex] = React.useState(() => getJobStatusColor(statusId));
  const hasOverride = hasJobStatusColorOverride(statusId);

  React.useEffect(() => {
    setHex(getJobStatusColor(statusId));
  }, [statusId, colorsRevision]);

  const { bgColor, textColor, borderColor } = getStatusChipStyles(statusId);
  const pickerId = `job-status-color-picker-${statusId}`;

  const applyHex = (value) => {
    if (!/^#[0-9A-Fa-f]{6}$/.test(value)) return;
    setHex(value);
    setJobStatusColor(statusId, value);
  };

  const handleHexBlur = () => {
    if (/^#[0-9A-Fa-f]{6}$/.test(hex)) return;
    if (/^#[0-9A-Fa-f]{3}$/.test(hex)) {
      const expanded =
        '#' +
        hex[1] +
        hex[1] +
        hex[2] +
        hex[2] +
        hex[3] +
        hex[3];
      setHex(expanded);
      setJobStatusColor(statusId, expanded);
      return;
    }
    setHex(getJobStatusColor(statusId));
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 2,
        py: 1.5,
      }}
    >
      <Typography
        sx={{
          minWidth: { xs: '100%', sm: 200 },
          fontSize: '0.875rem',
          fontWeight: 500,
          color: 'text.primary',
        }}
      >
        {status.label}
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, flex: 1 }}>
        <Box
          component="label"
          htmlFor={pickerId}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            px: 2,
            py: 1.25,
            borderRadius: 1,
            border: '1px solid',
            borderColor: isDarkMode ? '#334155' : '#e0e0e0',
            backgroundColor: isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
              borderColor: isDarkMode ? '#475569' : '#bdbdbd',
            },
          }}
        >
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: '6px',
              backgroundColor: hex,
              border: '2px solid',
              borderColor: isDarkMode ? '#475569' : '#cbd5e1',
            }}
          />
        </Box>
        <Box
          component="input"
          id={pickerId}
          type="color"
          value={hex}
          onChange={(e) => applyHex(e.target.value)}
          aria-label={`Choose color for ${status.label}`}
          sx={{
            position: 'absolute',
            width: '1px',
            height: '1px',
            opacity: 0,
            overflow: 'hidden',
            clip: 'rect(0, 0, 0, 0)',
            whiteSpace: 'nowrap',
            border: 0,
          }}
        />
        <TextField
          size="small"
          label="Hex"
          value={hex}
          onChange={(e) => {
            let v = e.target.value.trim();
            if (!v.startsWith('#')) v = '#' + v;
            if (/^#[0-9A-Fa-f]{0,6}$/.test(v) || v === '#') {
              setHex(v === '#' ? '#' : v);
              if (v.length === 7 && /^#[0-9A-Fa-f]{6}$/.test(v)) {
                setJobStatusColor(statusId, v);
              }
            }
          }}
          onBlur={handleHexBlur}
          sx={{
            width: 100,
            '& .MuiOutlinedInput-root': {
              borderRadius: 1,
              backgroundColor: 'background.paper',
              '& fieldset': { borderColor: isDarkMode ? '#334155' : '#e0e0e0' },
            },
            '& .MuiInputBase-input': { fontSize: '0.875rem', fontFamily: 'monospace' },
          }}
        />
        <Chip
          label={getStatusLabel(statusId)}
          size="small"
          sx={{
            backgroundColor: bgColor,
            color: textColor,
            fontSize: '0.75rem',
            fontWeight: 500,
            height: 24,
            borderRadius: '6px',
            border: `1px solid ${borderColor}`,
            '& .MuiChip-label': { padding: '0 8px' },
          }}
        />
        {hasOverride && (
          <Button
            size="small"
            variant="text"
            onClick={() => resetJobStatusColor(statusId)}
            sx={{ textTransform: 'none', fontSize: '0.8rem' }}
          >
            Reset
          </Button>
        )}
      </Box>
    </Box>
  );
}

function StoreNameRow({ storeName, isDarkMode, onUpdated, onDeleted, onError }) {
  const [editing, setEditing] = React.useState(false);
  const [draftName, setDraftName] = React.useState(storeName.name);
  const [saving, setSaving] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);

  React.useEffect(() => {
    if (!editing) setDraftName(storeName.name);
  }, [storeName.name, editing]);

  const handleSave = async () => {
    const trimmed = draftName.trim();
    if (!trimmed) {
      onError('Store name cannot be empty.');
      return;
    }
    if (trimmed === storeName.name) {
      setEditing(false);
      return;
    }

    setSaving(true);
    const updated = await updateStoreNameSuggestion(storeName.id, trimmed);
    setSaving(false);

    if (!updated) {
      onError('Failed to update store name. It may already exist.');
      return;
    }

    onUpdated(updated);
    setEditing(false);
  };

  const handleDelete = async () => {
    setDeleting(true);
    const removed = await removeStoreNameSuggestion(storeName.id);
    setDeleting(false);

    if (!removed) {
      onError('Failed to delete store name.');
      return;
    }

    onDeleted(storeName.id);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 1.5,
        py: 1.5,
      }}
    >
      {editing ? (
        <>
          <TextField
            size="small"
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            disabled={saving}
            sx={{
              flex: 1,
              minWidth: { xs: '100%', sm: 200 },
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
                backgroundColor: 'background.paper',
                '& fieldset': { borderColor: isDarkMode ? '#334155' : '#e0e0e0' },
              },
            }}
          />
          <IconButton
            size="small"
            color="primary"
            onClick={handleSave}
            disabled={saving}
            aria-label="Save store name"
          >
            {saving ? <CircularProgress size={18} /> : <CheckIcon fontSize="small" />}
          </IconButton>
          <IconButton
            size="small"
            onClick={() => {
              setDraftName(storeName.name);
              setEditing(false);
            }}
            disabled={saving}
            aria-label="Cancel editing store name"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </>
      ) : (
        <>
          <Typography sx={{ flex: 1, minWidth: { xs: '100%', sm: 200 }, fontSize: '0.875rem', fontWeight: 500, color: 'text.primary' }}>
            {storeName.name}
          </Typography>
          <IconButton
            size="small"
            onClick={() => setEditing(true)}
            aria-label={`Edit ${storeName.name}`}
            sx={{ color: 'text.secondary' }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={handleDelete}
            disabled={deleting}
            aria-label={`Delete ${storeName.name}`}
            sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
          >
            {deleting ? <CircularProgress size={18} /> : <DeleteIcon fontSize="small" />}
          </IconButton>
        </>
      )}
    </Box>
  );
}

function StoreNamesSection({ isDarkMode }) {
  const [storeNames, setStoreNames] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [newName, setNewName] = React.useState('');
  const [adding, setAdding] = React.useState(false);
  const [error, setError] = React.useState('');

  const loadStoreNames = React.useCallback(async () => {
    setLoading(true);
    const items = await fetchStoreNameSuggestions();
    setStoreNames(sortStoreNameOptions(items));
    setLoading(false);
  }, []);

  React.useEffect(() => {
    loadStoreNames();
  }, [loadStoreNames]);

  const handleAdd = async () => {
    const trimmed = newName.trim();
    if (!trimmed) {
      setError('Enter a store name.');
      return;
    }

    setAdding(true);
    setError('');
    const created = await addStoreNameSuggestion(trimmed);
    setAdding(false);

    if (!created) {
      setError('Failed to add store name. It may already exist.');
      return;
    }

    setStoreNames((prev) => sortStoreNameOptions([...prev.filter((item) => item.id !== created.id), created]));
    setNewName('');
  };

  const handleUpdated = (updated) => {
    setStoreNames((prev) => sortStoreNameOptions(prev.map((item) => (item.id === updated.id ? updated : item))));
    setError('');
  };

  const handleDeleted = (id) => {
    setStoreNames((prev) => prev.filter((item) => item.id !== id));
    setError('');
  };

  return (
    <>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
        <TextField
          size="small"
          label="New store name"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          disabled={adding}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleAdd();
            }
          }}
          sx={{
            flex: 1,
            minWidth: { xs: '100%', sm: 220 },
            '& .MuiOutlinedInput-root': {
              borderRadius: 1,
              backgroundColor: 'background.paper',
              '& fieldset': { borderColor: isDarkMode ? '#334155' : '#e0e0e0' },
            },
          }}
        />
        <Button
          variant="contained"
          onClick={handleAdd}
          disabled={adding || !newName.trim()}
          sx={{ textTransform: 'none', fontWeight: 600, minWidth: 88 }}
        >
          {adding ? 'Adding…' : 'Add'}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : storeNames.length === 0 ? (
        <Typography variant="body2" sx={{ color: 'text.secondary', py: 2 }}>
          No store names yet. Add one above or save a job with a store name.
        </Typography>
      ) : (
        <Box>
          {storeNames.map((storeName, index) => (
            <React.Fragment key={storeName.id}>
              {index > 0 && <Divider sx={{ borderColor: isDarkMode ? '#334155' : '#e0e0e0' }} />}
              <StoreNameRow
                storeName={storeName}
                isDarkMode={isDarkMode}
                onUpdated={handleUpdated}
                onDeleted={handleDeleted}
                onError={setError}
              />
            </React.Fragment>
          ))}
        </Box>
      )}
    </>
  );
}

function TagColorPicker({ color, onChange, isDarkMode, pickerId, ariaLabel }) {
  const theme = useTheme();
  const swatchColor = color || (theme.palette.mode === 'dark' ? '#475569' : '#e0e0e0');

  return (
    <>
      <Box
        component="label"
        htmlFor={pickerId}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          px: 1.5,
          py: 1,
          borderRadius: 1,
          border: '1px solid',
          borderColor: isDarkMode ? '#334155' : '#e0e0e0',
          backgroundColor: isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
            borderColor: isDarkMode ? '#475569' : '#bdbdbd',
          },
        }}
      >
        <Box
          sx={{
            width: 24,
            height: 24,
            borderRadius: '6px',
            backgroundColor: swatchColor,
            border: '2px solid',
            borderColor: isDarkMode ? '#475569' : '#cbd5e1',
          }}
        />
      </Box>
      <Box
        component="input"
        id={pickerId}
        type="color"
        value={color || '#64748b'}
        onChange={(e) => onChange(e.target.value)}
        aria-label={ariaLabel}
        sx={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          opacity: 0,
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      />
    </>
  );
}

function TagRow({ tag, isDarkMode, onUpdated, onDeleted, onError }) {
  const theme = useTheme();
  const [editing, setEditing] = React.useState(false);
  const [draftName, setDraftName] = React.useState(tag.name);
  const [draftColor, setDraftColor] = React.useState(tag.color || '');
  const [saving, setSaving] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);
  const pickerId = `job-tag-color-picker-${tag.id}`;

  React.useEffect(() => {
    if (!editing) {
      setDraftName(tag.name);
      setDraftColor(tag.color || '');
    }
  }, [tag.name, tag.color, editing]);

  const handleSave = async () => {
    const trimmed = draftName.trim();
    if (!trimmed) {
      onError('Tag name cannot be empty.');
      return;
    }

    const nextColor = draftColor || null;
    if (trimmed === tag.name && nextColor === (tag.color || null)) {
      setEditing(false);
      return;
    }

    setSaving(true);
    const updated = await updateJobTag(tag.id, { name: trimmed, color: nextColor });
    setSaving(false);

    if (!updated) {
      onError('Failed to update tag. It may already exist.');
      return;
    }

    onUpdated(updated);
    setEditing(false);
  };

  const handleDelete = async () => {
    setDeleting(true);
    const removed = await removeJobTag(tag.id);
    setDeleting(false);

    if (!removed) {
      onError('Failed to delete tag.');
      return;
    }

    onDeleted(tag.id);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 1.5,
        py: 1.5,
      }}
    >
      {editing ? (
        <>
          <TextField
            size="small"
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            disabled={saving}
            sx={{
              flex: 1,
              minWidth: { xs: '100%', sm: 200 },
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
                backgroundColor: 'background.paper',
                '& fieldset': { borderColor: isDarkMode ? '#334155' : '#e0e0e0' },
              },
            }}
          />
          <TagColorPicker
            color={draftColor}
            onChange={setDraftColor}
            isDarkMode={isDarkMode}
            pickerId={pickerId}
            ariaLabel={`Choose color for ${tag.name}`}
          />
          <IconButton
            size="small"
            color="primary"
            onClick={handleSave}
            disabled={saving}
            aria-label="Save tag"
          >
            {saving ? <CircularProgress size={18} /> : <CheckIcon fontSize="small" />}
          </IconButton>
          <IconButton
            size="small"
            onClick={() => {
              setDraftName(tag.name);
              setDraftColor(tag.color || '');
              setEditing(false);
            }}
            disabled={saving}
            aria-label="Cancel editing tag"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </>
      ) : (
        <>
          <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: 200 }, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={tag.name}
              size="small"
              sx={getTagChipSx(tag, theme)}
            />
            {!tag.is_active && (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Inactive
              </Typography>
            )}
          </Box>
          <IconButton
            size="small"
            onClick={() => setEditing(true)}
            aria-label={`Edit ${tag.name}`}
            sx={{ color: 'text.secondary' }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={handleDelete}
            disabled={deleting}
            aria-label={`Delete ${tag.name}`}
            sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
          >
            {deleting ? <CircularProgress size={18} /> : <DeleteIcon fontSize="small" />}
          </IconButton>
        </>
      )}
    </Box>
  );
}

function TagsSection({ isDarkMode }) {
  const [tags, setTags] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [newName, setNewName] = React.useState('');
  const [newColor, setNewColor] = React.useState('');
  const [adding, setAdding] = React.useState(false);
  const [error, setError] = React.useState('');

  const loadTags = React.useCallback(async () => {
    setLoading(true);
    const items = await fetchJobTags({ activeOnly: false });
    setTags(sortTagOptions(items));
    setLoading(false);
  }, []);

  React.useEffect(() => {
    loadTags();
  }, [loadTags]);

  const handleAdd = async () => {
    const trimmed = newName.trim();
    if (!trimmed) {
      setError('Enter a tag name.');
      return;
    }

    setAdding(true);
    setError('');
    const created = await addJobTag(trimmed, newColor || null);
    setAdding(false);

    if (!created) {
      setError('Failed to add tag. It may already exist.');
      return;
    }

    setTags((prev) => sortTagOptions([...prev.filter((item) => item.id !== created.id), created]));
    setNewName('');
    setNewColor('');
  };

  const handleUpdated = (updated) => {
    setTags((prev) => sortTagOptions(prev.map((item) => (item.id === updated.id ? updated : item))));
    setError('');
  };

  const handleDeleted = (id) => {
    setTags((prev) => prev.filter((item) => item.id !== id));
    setError('');
  };

  return (
    <>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2, alignItems: 'center' }}>
        <TextField
          size="small"
          label="New tag"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          disabled={adding}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleAdd();
            }
          }}
          sx={{
            flex: 1,
            minWidth: { xs: '100%', sm: 220 },
            '& .MuiOutlinedInput-root': {
              borderRadius: 1,
              backgroundColor: 'background.paper',
              '& fieldset': { borderColor: isDarkMode ? '#334155' : '#e0e0e0' },
            },
          }}
        />
        <TagColorPicker
          color={newColor}
          onChange={setNewColor}
          isDarkMode={isDarkMode}
          pickerId="job-tag-new-color-picker"
          ariaLabel="Choose color for new tag"
        />
        <Button
          variant="contained"
          onClick={handleAdd}
          disabled={adding || !newName.trim()}
          sx={{ textTransform: 'none', fontWeight: 600, minWidth: 88 }}
        >
          {adding ? 'Adding…' : 'Add'}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : tags.length === 0 ? (
        <Typography variant="body2" sx={{ color: 'text.secondary', py: 2 }}>
          No tags yet. Add one above to get started.
        </Typography>
      ) : (
        <Box>
          {tags.map((tag, index) => (
            <React.Fragment key={tag.id}>
              {index > 0 && <Divider sx={{ borderColor: isDarkMode ? '#334155' : '#e0e0e0' }} />}
              <TagRow
                tag={tag}
                isDarkMode={isDarkMode}
                onUpdated={handleUpdated}
                onDeleted={handleDeleted}
                onError={setError}
              />
            </React.Fragment>
          ))}
        </Box>
      )}
    </>
  );
}

function CompanyProfileSettingsWrapper({ user, isDarkMode }) {
  const [message, setMessage] = React.useState(null);
  const [severity, setSeverity] = React.useState('success');

  const handleSaved = (msg) => {
    setSeverity('success');
    setMessage(msg);
  };
  const handleError = (msg) => {
    setSeverity('error');
    setMessage(msg);
  };

  return (
    <Box>
      {message && (
        <Alert severity={severity} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message}
        </Alert>
      )}
      <CompanyProfileSection
        mode="tenant"
        user={user}
        onSaved={handleSaved}
        onError={handleError}
      />
    </Box>
  );
}

export default function Settings() {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const user = useSelector(getUser);
  const canManageStoreNames = isManagement(user);
  const canManageTags = isManagement(user);
  const [colorsRevision, setColorsRevision] = React.useState(0);

  const statuses = React.useMemo(
    () => [...getAllStatuses()].sort((a, b) => a.label.localeCompare(b.label)),
    []
  );

  React.useEffect(() => {
    const onChange = () => setColorsRevision((r) => r + 1);
    window.addEventListener(JOB_STATUS_COLORS_CHANGED_EVENT, onChange);
    return () => window.removeEventListener(JOB_STATUS_COLORS_CHANGED_EVENT, onChange);
  }, []);

  const handleResetAll = () => {
    resetAllJobStatusColors();
  };

  return (
    <Paper
      elevation={0}
      sx={{
        height: '100vh',
        width: '100%',
        padding: { xs: 1.5, md: 3 },
        backgroundColor: 'background.default',
        overflow: 'auto',
      }}
    >
      <Typography
        variant="h4"
        component="h1"
        sx={{
          fontWeight: 700,
          fontSize: '2rem',
          color: 'text.primary',
          mb: 3,
        }}
      >
        Settings
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <SettingsSection
          title="Notifications"
          description="Per-company channels for in-app and email. WhatsApp coming soon. Changes save automatically."
          isDarkMode={isDarkMode}
        >
          <NotificationPreferencesSection isDarkMode={isDarkMode} />
        </SettingsSection>

        <SettingsSection
          title="Mobile bottom navigation"
          description="Customize the quick tabs shown at the bottom of the screen on phones and tablets when the menu is closed."
          isDarkMode={isDarkMode}
        >
          <MobileBottomTabsSection user={user} isDarkMode={isDarkMode} />
        </SettingsSection>

        <SettingsSection
          title="Work order status colors"
          description="Used for status chips in Work Orders, Accounting, and job details. Stored on this device."
          isDarkMode={isDarkMode}
        >
          <Box>
            {statuses.map((status, index) => (
              <React.Fragment key={status.id}>
                {index > 0 && <Divider sx={{ borderColor: isDarkMode ? '#334155' : '#e0e0e0' }} />}
                <StatusColorRow
                  status={status}
                  isDarkMode={isDarkMode}
                  colorsRevision={colorsRevision}
                />
              </React.Fragment>
            ))}
          </Box>

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={handleResetAll}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderColor: isDarkMode ? '#475569' : '#e0e0e0',
                color: 'text.primary',
              }}
            >
              Reset all to defaults
            </Button>
          </Box>
        </SettingsSection>

        {canManageStoreNames && (
          <SettingsSection
            title="Store names"
            description="Shared store name suggestions for job autocomplete. Visible to all users; only owners and general managers can manage this list."
            isDarkMode={isDarkMode}
          >
            <StoreNamesSection isDarkMode={isDarkMode} />
          </SettingsSection>
        )}

        {canManageTags && (
          <SettingsSection
            title="Job tags"
            description="Manage tags used on work orders. Only owners and general managers can view and manage this list."
            isDarkMode={isDarkMode}
          >
            <TagsSection isDarkMode={isDarkMode} />
          </SettingsSection>
        )}

        {canEditCompanyProfile(user) && (
          <SettingsSection
            title="Company profile"
            description="Company address, contact info, logo, and documents. Used on invoices and visible to admins/dispatchers when marked public."
            isDarkMode={isDarkMode}
          >
            <CompanyProfileSettingsWrapper user={user} isDarkMode={isDarkMode} />
          </SettingsSection>
        )}
      </Box>
    </Paper>
  );
}
